import * as React from 'react';
import {FunctionComponent} from 'react';
import {observer} from 'mobx-react-lite';
import {PresenterProps, withPresenter} from '../../../../Support/with_presenter';
import {TrendReportPresenter} from './TrendReportPresenter';
import {Link} from 'react-router-dom';
import {ErrorPlaceholder} from '../../../Components/Utils/ErrorPlaceholder';
import {Loader} from '../../../Components/Utils/Loader';
import {DateRange} from '../../../../Domain/DateRange';
import {Report} from '../../../../Models/Report';
import {Button} from '../../../Components/Button';
import {Feedback} from '../../../Components/Feedback';
import {PostProcessing} from '../PostProcessing';
import {TrendReportContent} from './TrendReportContent';
import {TrendTemplate} from './Models/ReportTemplate';
import {CompareReportContent} from '../Compare/CompareReportContent';
import {CompareSelector} from './Sidebar/CompareSelector';
import {FeatureFlags} from '../../../../FeatureFlags';
import {classNames} from '../../../Support/classNames';
import {BrowseWorkouts} from '../Sidebar/BrowseWorkouts';

interface OwnProps {
    report: Report;
    dateRange: DateRange | null;
}

const TrendReportComponent: FunctionComponent<OwnProps & PresenterProps<TrendReportPresenter>> = observer(
    ({report, presenter}) => {
        if (presenter.result.status === 'loading') {
            return (
                <div className="container">
                    <div className="row">
                        <div className="col-12 d-flex flex-row justify-content-start">
                            <Link to="/reports" className="btn btn-back-arrow">
                                <i className="bi bi-arrow-left"></i>
                            </Link>
                            <h1>Trend report</h1>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-12">
                            <Loader className="mt-5" text={presenter.result.message || null} />
                        </div>
                    </div>
                </div>
            );
        }

        if (presenter.result.status === 'error') {
            return (
                <div className="container">
                    <div className="row">
                        <div className="col-12 d-flex flex-row justify-content-start">
                            <Link to="/reports" className="btn btn-back-arrow">
                                <i className="bi bi-arrow-left"></i>
                            </Link>
                            <h1>Trend report</h1>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-12">
                            <ErrorPlaceholder large className="mt-5" />
                        </div>
                    </div>
                </div>
            );
        }

        const reportData = presenter.result.data;

        if (reportData.snippets.length === 0) {
            return (
                <div className="container">
                    <div className="row">
                        <div className="col-12">
                            <div className="row">
                                <div className="col-12 d-flex flex-row justify-content-start">
                                    <Link to="/reports" className="btn btn-back-arrow">
                                        <i className="bi bi-arrow-left"></i>
                                    </Link>
                                    <h1>Trend report</h1>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-12 col-md-9">
                                    <div className="col-12">
                                        <div className="card">
                                            <div className="card-body">
                                                <span className="workoutlist__empty">
                                                    <span className="workoutlist__empty_image" />
                                                    <span className="workoutlist__empty_text">
                                                        <p>Empty report</p>
                                                        <small>
                                                            Start building your report by adding your own{' '}
                                                            <Link to="/workouts">workouts</Link> or selected ranges from
                                                            them.
                                                            {FeatureFlags.crews && (
                                                                <>
                                                                    {' '}
                                                                    You can also include workouts from your{' '}
                                                                    <Link to="/crews">crewmates</Link>.
                                                                </>
                                                            )}
                                                            <>
                                                                {' '}
                                                                Use the workout sidebar to add a full workout or a
                                                                specific range.
                                                            </>
                                                        </small>
                                                    </span>
                                                    <Link to="/workouts" className="btn-primary btn">
                                                        Browse Workouts
                                                    </Link>
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-12 col-md-3">
                                    <div style={{position: 'sticky', top: 24}}>
                                        <CompareSelector
                                            mode={presenter.mode}
                                            onChangeMode={(mode) => presenter.changeMode(mode)}
                                            selectedSnippetIds={presenter.selectedSnippetIds}
                                            splitStrategy={presenter.splitStrategy}
                                            onChangeSplitStrategy={(sS) => {
                                                presenter.changeSplitStrategy(sS);
                                            }}
                                            onResetSelectedSnippetIds={() => presenter.updateSelectedSnippetIds([])}
                                            className="mb-3"
                                        />
                                        <BrowseWorkouts className="mb-3" />
                                        <Feedback className="mb-3" />
                                        {report && (
                                            <div className="row mb-3">
                                                <div className="col-12 text-center">
                                                    <Button
                                                        text={
                                                            <>
                                                                <i className="bi-trash me-2" />
                                                                Delete report
                                                            </>
                                                        }
                                                        onClick={() => {
                                                            if (
                                                                confirm('Are you sure you want to delete this report?')
                                                            ) {
                                                                presenter.delete();
                                                            }
                                                        }}
                                                        loading={presenter.deleting}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div className="container">
                <div className="row">
                    <div className="col-12">
                        <div className="row">
                            <div className="col-12 col-md-9 d-flex flex-row justify-content-start">
                                <Link to="/reports" className="btn btn-back-arrow">
                                    <i className="bi bi-arrow-left"></i>
                                </Link>
                                {!presenter.isEditingName && (
                                    <div className="d-flex flex-row justify-content-between w-100">
                                        <h1>
                                            {presenter.name === '' ? 'Trend report' : presenter.name}
                                            {!presenter.isLocked && (
                                                <button
                                                    className="btn btn-link"
                                                    onClick={() => presenter.startEditName()}
                                                >
                                                    <i className="bi bi-pencil"></i>
                                                </button>
                                            )}
                                        </h1>
                                        <div className="form-check mt-2 form-switch d-flex justify-content-center align-content-center flex-wrap">
                                            <label className="form-check-label me-5" htmlFor="lock">
                                                {presenter.isLocked ? 'Locked' : 'Lock template'}
                                            </label>
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                role="switch"
                                                id="lock"
                                                checked={presenter.isLocked}
                                                onChange={() => presenter.toggleLock()}
                                            />
                                        </div>
                                    </div>
                                )}
                                {presenter.isEditingName && (
                                    <h1>
                                        <form
                                            className="d-flex flex-row justify-content-start"
                                            onSubmit={(e) => {
                                                e.preventDefault();
                                                if (!presenter.isSaving) {
                                                    presenter.submit();
                                                }
                                            }}
                                        >
                                            <input
                                                autoFocus
                                                disabled={presenter.isSaving}
                                                type="text"
                                                className="form-control"
                                                value={presenter.name}
                                                onKeyUp={(e) => {
                                                    if (e.key === 'Escape') {
                                                        presenter.stopEditName();
                                                    }
                                                }}
                                                onChange={(e) => {
                                                    presenter.updateName(e.target.value);
                                                }}
                                            />
                                            <button type="submit" className="btn btn-link">
                                                <i className="bi bi-check"></i>
                                            </button>
                                        </form>
                                    </h1>
                                )}
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-12 col-md-9">
                                <div className="row mb-3">
                                    <div className="col-12">
                                        <div className="form-group">
                                            {presenter.isLocked && presenter.description !== '' && (
                                                <p>{presenter.description}</p>
                                            )}
                                            {!presenter.isLocked && (
                                                <textarea
                                                    className="form-control"
                                                    id="description"
                                                    placeholder="Comment area for your trend report..."
                                                    value={presenter.description}
                                                    onChange={(e) => presenter.updateDescription(e.target.value)}
                                                />
                                            )}
                                        </div>
                                    </div>
                                </div>
                                {presenter.mode === 'trend' && (
                                    <TrendReportContent
                                        template={presenter.template}
                                        isLocked={presenter.isLocked}
                                        startAddingTrend={() => presenter.startAddingTrend()}
                                        selectedSnippetIds={presenter.selectedSnippetIds}
                                        highlightedSnippetIds={presenter.highlightedSnippetIds}
                                        reportData={presenter.result.data}
                                        updateSelectedSnippetIds={(snippetIds) =>
                                            presenter.updateSelectedSnippetIds(snippetIds)
                                        }
                                        updateHighlightedSnippetIds={(snippetIds) =>
                                            presenter.updateHighlightedSnippetIds(snippetIds)
                                        }
                                        updateTrendTemplate={(index: number, trendTemplate: TrendTemplate) => {
                                            presenter.updateTrendTemplate(index, trendTemplate);
                                        }}
                                        deleteTrendTemplate={(index) => presenter.deleteTrendTemplate(index)}
                                        onDeleteSnippet={(snippetId) => presenter.removeSnippet(snippetId)}
                                        addTrendTemplate={(trendTemplate) => presenter.addTrendTemplate(trendTemplate)}
                                        adding={presenter.adding}
                                        stopAddingTrend={() => presenter.stopAddingTrend()}
                                    />
                                )}
                                {presenter.mode === 'comparing' && (
                                    <CompareReportContent
                                        key={presenter.selectedSnippetIds.join('-')}
                                        snippets={presenter.selectedSnippets}
                                        splitStrategy={presenter.splitStrategy}
                                    />
                                )}
                            </div>
                            <div className="col-12 col-md-3">
                                <div style={{position: 'sticky', top: 24}}>
                                    <PostProcessing reportData={reportData} className="mb-3" />
                                    <CompareSelector
                                        mode={presenter.mode}
                                        onChangeMode={(mode) => presenter.changeMode(mode)}
                                        selectedSnippetIds={presenter.selectedSnippetIds}
                                        splitStrategy={presenter.splitStrategy}
                                        onChangeSplitStrategy={(sS) => {
                                            presenter.changeSplitStrategy(sS);
                                        }}
                                        onResetSelectedSnippetIds={() => presenter.updateSelectedSnippetIds([])}
                                        className="mb-3"
                                    />
                                    <BrowseWorkouts className="mb-3" />
                                    <Feedback className="mb-3" />
                                    {report && (
                                        <div className="row mb-3">
                                            <div className="col-12 text-center">
                                                <Button
                                                    text={
                                                        <>
                                                            <i className="bi-trash me-2" />
                                                            Delete report
                                                        </>
                                                    }
                                                    onClick={() => {
                                                        if (confirm('Are you sure you want to delete this report?')) {
                                                            presenter.delete();
                                                        }
                                                    }}
                                                    loading={presenter.deleting}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    },
);

export const TrendReport = withPresenter<TrendReportPresenter, OwnProps>(
    (props, component) =>
        new TrendReportPresenter(props.report, props.dateRange, component.network.snippets, component.network.report),
    TrendReportComponent,
);
